import PageLayout from "@/components/layout/PageLayout";
import { createFileRoute } from "@tanstack/react-router";
import { Chain } from "@nitro-ape/shared/models/enums/chain";
import {
  useChainHelpers,
  useChainNativeTokenSymbol,
} from "@nitro-ape/shared/features/config/api/get-chain-helpers";
import { useWalletHelpers } from "@nitro-ape/shared/features/wallets/api/get-balance-helpers";
import { useHyperliquidWallet } from "@nitro-ape/shared/features/wallets/api/get-wallets";
import ChainWalletCard from "./-components/ChainWalletCard";
import { WalletTag } from "@nitro-ape/shared/models/enums/wallet-tag";
import {
  AddFundsDialog,
  TopupType,
} from "../-components/modals/AddFundsDialog";
import { WithdrawalDialogWrapper } from "../wallets/-components/modals/WithdrawalDialog";
import WalletTokensList from "./-components/WalletTokensList";
import { useState } from "react";
import MonacoLogo from "@/components/logos/currencies/MonacoLogo";
import { ChainLogo } from "@/components/logos/currencies/ChainLogo";
import { useMonacoBalances } from "@nitro-ape/shared/features/monaco/api/get-monaco-balances";
import { useMonacoPrimaryMarginAccount } from "@nitro-ape/shared/features/monaco-perps/api/get-monaco-margin-account";
import { useMonacoMarginSummary } from "@nitro-ape/shared/features/monaco-perps/api/get-monaco-margin-summary";
import { usePrivy } from "@privy-io/react-auth";
import { MonacoTestnetSetup } from "@/components/trading/MonacoTestnetSetup";
import { HYPERLIQUID_ENABLED } from "@/lib/feature-flags";

export const Route = createFileRoute("/_main/wallet/")({
  head: () => ({
    meta: [{ title: "Cultz | Wallet" }],
  }),
  component: WalletPage,
});

function WalletPage() {
  const { getTotalNativeBalance } = useWalletHelpers();
  const hyperliquidWallet = useHyperliquidWallet();
  const { data: monacoBalances } = useMonacoBalances({});
  const { marginAccountId } = useMonacoPrimaryMarginAccount();
  const { data: monacoMarginSummary } = useMonacoMarginSummary({
    marginAccountId,
  });
  const { ready, authenticated, user, exportWallet } = usePrivy();
  const [topUpDialog, setTopUpDialog] = useState<{
    open: boolean;
    tab?: TopupType;
    visibleTabs?: TopupType[];
  }>({ open: false });
  const [withdrawDialog, setWithdrawDialog] = useState<{
    open: boolean;
    chain?: Chain;
    visibleTabs?: Array<"solana" | "perps" | "monaco">;
  }>({ open: false });

  // Check if user can export wallet
  const isAuthenticated = ready && authenticated;
  const hasEmbeddedWallet = !!user?.linkedAccounts?.find(
    (account) =>
      account.type === "wallet" &&
      "walletClientType" in account &&
      account.walletClientType === "privy" &&
      "chainType" in account &&
      account.chainType === "ethereum",
  );
  const canExportWallet = isAuthenticated && hasEmbeddedWallet;

  const solSymbol = useChainNativeTokenSymbol(Chain.solana);
  const seiSymbol = useChainNativeTokenSymbol(Chain.sei);
  const hyperliquidSymbol = "USDC";
  const monacoSymbol = "USDC";

  const { native: solBalance } = getTotalNativeBalance(Chain.solana);
  const { native: seiBalance } = getTotalNativeBalance(Chain.sei);

  const hyperliquidBalance = (() => {
    const w = hyperliquidWallet;
    if (!w) return 0;
    const nativeItems = w.balances?.items?.filter((i) => i.is_native) ?? [];
    return nativeItems.reduce((sum, i) => sum + i.balance, 0);
  })();

  // Sum USDC available_balance across Monaco spot balances (USDC ≈ $1).
  // Sourced from the React Query cache so transfers, deposits, and websocket
  // movement events keep this in sync with the rest of the wallet page.
  const monacoSpotBalance = (() => {
    if (!monacoBalances?.length) return 0;
    return monacoBalances.reduce((sum, b) => {
      if ((b.symbol ?? "").toUpperCase() !== "USDC") return sum;
      const value =
        typeof b.available_balance === "string"
          ? parseFloat(b.available_balance)
          : Number(b.available_balance);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
  })();
  const monacoPerpsBalance = (() => {
    const equity = Number(monacoMarginSummary?.equity ?? 0);
    return Number.isFinite(equity) ? equity : 0;
  })();
  const monacoTotalBalance = monacoSpotBalance + monacoPerpsBalance;

  const openTopUpDialog = (tab: TopupType) => {
    setTopUpDialog({ open: true, tab, visibleTabs: [tab] });
  };

  const openWithdrawDialog = (
    chain: Chain | undefined,
    tabs: Array<"solana" | "perps" | "monaco">,
  ) => {
    setWithdrawDialog({ open: true, chain, visibleTabs: tabs });
  };

  const handleTopUpDialogChange = (open: boolean) => {
    setTopUpDialog((prev) => ({ ...prev, open }));
  };

  const handleWithdrawDialogChange = (open: boolean) => {
    setWithdrawDialog((prev) => ({ ...prev, open }));
  };

  return (
    <PageLayout>
      <div className="flex h-full w-full items-center justify-center">
        <div className="mx-auto flex h-full max-h-[670px] w-full max-w-[1550px] flex-col gap-1.5 xl:flex-row">
          {/* Left column: fixed width card area using rows; cards fill height */}
          <div className="h-full w-full xl:w-[664px] xl:shrink-0">
            <div className="flex h-full flex-col items-stretch gap-1">
              <div className="flex flex-1 items-stretch gap-1">

                <ChainWalletCard
                  className="h-full"
                  icon={
                    <ChainLogo
                      chain={Chain.sei}
                      className="h-[18px] w-[18px]"
                    />
                  }
                  name="Sei"
                  balance={seiBalance}
                  symbol={seiSymbol}
                  tags={[WalletTag.SPOT]}
                  onTopUp={() => openTopUpDialog(TopupType.SEI)}
                  onWithdraw={() => openWithdrawDialog(Chain.sei, ["solana"])}
                  onExportWallet={canExportWallet ? () => exportWallet() : undefined}
                />
                <ChainWalletCard
                  className="h-full"
                  icon={<MonacoLogo className="h-[12px] w-[12px] text-white" />}
                  name="Monaco"
                  balance={monacoTotalBalance}
                  symbol={monacoSymbol}
                  tags={[WalletTag.SPOT, WalletTag.PERPS]}
                  onTopUp={() => openTopUpDialog(TopupType.MONACO)}
                  onWithdraw={() => openWithdrawDialog(Chain.monaco_spot, ["monaco"])}
                />
              </div>
              <MonacoTestnetSetup variant="card" />
              {HYPERLIQUID_ENABLED && (
                <div className="flex flex-1 items-stretch gap-1">
                  <ChainWalletCard
                    className="h-full"
                    icon={
                      <ChainLogo
                        chain={Chain.hyperliquid}
                        className="h-[18px] w-[18px]"
                      />
                    }
                    name="Hyperliquid"
                    balance={hyperliquidBalance}
                    symbol={hyperliquidSymbol}
                    tags={[WalletTag.SPOT, WalletTag.PERPS]}
                    onTopUp={() => openTopUpDialog(TopupType.PERPS)}
                    onWithdraw={() => openWithdrawDialog(undefined, ["perps"])}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right column: takes remaining space */}
          <div className="min-h-[160px] w-full min-w-0 flex-1">
            <WalletTokensList />
          </div>
        </div>
      </div>
      <AddFundsDialog
        open={topUpDialog.open}
        onOpenChange={handleTopUpDialogChange}
        initialTab={topUpDialog.tab}
        visibleTabs={topUpDialog.visibleTabs}
      />
      <WithdrawalDialogWrapper
        open={withdrawDialog.open}
        onOpenChange={handleWithdrawDialogChange}
        chainOverride={withdrawDialog.chain}
        visibleTabs={withdrawDialog.visibleTabs}
      />
    </PageLayout>
  );
}
